import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  MapPin, 
  Train, 
  School, 
  TreePine, 
  ShoppingBag,
  Loader2,
  Navigation,
  Clock,
  Car
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const AMENITY_CONFIG = {
  train_stations: { icon: Train, color: '#3B82F6', label: 'Train Stations' },
  parks: { icon: TreePine, color: '#22C55E', label: 'Parks' },
  retail: { icon: ShoppingBag, color: '#F59E0B', label: 'Shops' },
  primary_schools: { icon: School, color: '#8B5CF6', label: 'Primary Schools' },
  secondary_schools: { icon: School, color: '#EC4899', label: 'Secondary Schools' },
  high_street: { icon: ShoppingBag, color: '#EF4444', label: 'High Street' }
};

export default function AmenitiesDialog({ open, onClose, inspection }) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [amenitiesData, setAmenitiesData] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [radius, setRadius] = useState(2000);
  const [activeTab, setActiveTab] = useState('map');
  const [selectedTypes, setSelectedTypes] = useState(['train_stations', 'parks', 'retail']);
  const [geocodeError, setGeocodeError] = useState(null);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  useEffect(() => {
    if (open && inspection) {
      setGeocodeError(null);
      setShowManualEntry(false);
      fetchAmenities();
    }
  }, [open, inspection]);

  const fetchAmenities = async () => {
    setLoading(true);
    setGeocodeError(null);
    try {
      const response = await axios.get(`${API}/amenities/inspection/${inspection.id}`, {
        params: { radius },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAmenitiesData(response.data);
      setCoordinates(response.data.coordinates);
    } catch (error) {
      // If geocoding fails, show manual entry option
      if (error.response?.status === 400 || error.response?.status === 404) {
        setGeocodeError(error.response?.data?.detail || 'Could not locate property');
        setShowManualEntry(true);
      } else {
        toast.error('Failed to fetch amenities');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchWithManualCoords = async () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Please enter valid coordinates');
      return;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Coordinates out of range');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/amenities`, {
        params: { lat, lng, radius },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform response to match expected format
      setAmenitiesData({
        ...response.data,
        coordinates: { lat, lng },
        full_amenities: response.data.amenities
      });
      setCoordinates({ lat, lng });
      setShowManualEntry(false);
      setGeocodeError(null);
      
      // Save coordinates to inspection for future use
      try {
        await axios.put(`${API}/inspections/${inspection.id}`, {
          property_details: {
            ...inspection.property_details,
            coordinates: { lat, lng }
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.log('Could not cache coordinates');
      }
      
    } catch (error) {
      toast.error('Failed to fetch amenities');
    } finally {
      setLoading(false);
    }
  };

  const toggleAmenityType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const getFilteredAmenities = () => {
    if (!amenitiesData?.full_amenities) return [];
    
    const filtered = [];
    selectedTypes.forEach(type => {
      const items = amenitiesData.full_amenities[type] || [];
      items.forEach(item => {
        filtered.push({ ...item, type });
      });
    });
    return filtered;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-lime-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Limited';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="amenities-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-accent" />
            Local Amenities
          </DialogTitle>
          <DialogDescription>
            {inspection?.property_address}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
            <span className="ml-2 text-muted-foreground">Loading amenities...</span>
          </div>
        ) : amenitiesData ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Location Score */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Location Score</p>
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold ${getScoreColor(amenitiesData.location_score)}`}>
                    {amenitiesData.location_score?.toFixed(0) || 0}
                  </span>
                  <span className="text-lg text-muted-foreground">/ 100</span>
                  <Badge variant="outline" className={getScoreColor(amenitiesData.location_score)}>
                    {getScoreLabel(amenitiesData.location_score)}
                  </Badge>
                </div>
              </div>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(AMENITY_CONFIG).slice(0, 3).map(([type, config]) => {
                  const IconComponent = config.icon;
                  const isSelected = selectedTypes.includes(type);
                  return (
                    <Button
                      key={type}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleAmenityType(type)}
                      className="gap-1"
                      style={{ 
                        backgroundColor: isSelected ? config.color : 'transparent',
                        borderColor: config.color
                      }}
                      data-testid={`filter-${type}`}
                    >
                      <IconComponent className="h-3 w-3" />
                      {config.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="map" data-testid="map-tab">Map View</TabsTrigger>
                <TabsTrigger value="list" data-testid="list-tab">List View</TabsTrigger>
              </TabsList>

              <TabsContent value="map" className="flex-1 mt-4">
                <div className="h-[400px] rounded-lg overflow-hidden border" data-testid="amenities-map">
                  {coordinates && (
                    <MapContainer
                      center={[coordinates.lat, coordinates.lng]}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      
                      {/* Property marker */}
                      <Marker position={[coordinates.lat, coordinates.lng]}>
                        <Popup>
                          <strong>Property Location</strong><br />
                          {inspection?.property_address}
                        </Popup>
                      </Marker>
                      
                      {/* Search radius circle */}
                      <Circle
                        center={[coordinates.lat, coordinates.lng]}
                        radius={radius}
                        pathOptions={{ 
                          color: '#0EA5E9', 
                          fillColor: '#0EA5E9', 
                          fillOpacity: 0.1 
                        }}
                      />
                      
                      {/* Amenity markers */}
                      {getFilteredAmenities().map((amenity, idx) => {
                        const config = AMENITY_CONFIG[amenity.type];
                        return (
                          <Marker
                            key={`${amenity.type}-${idx}`}
                            position={[amenity.lat, amenity.lng]}
                            icon={createIcon(config?.color || '#666')}
                          >
                            <Popup>
                              <div className="min-w-[150px]">
                                <strong>{amenity.name}</strong>
                                <p className="text-sm text-gray-600">{config?.label}</p>
                                <div className="mt-2 text-xs">
                                  <p>{amenity.distance_km?.toFixed(2)} km away</p>
                                  <p>{amenity.walk_time_min} min walk</p>
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="list" className="flex-1 mt-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedTypes.map((type) => {
                    const config = AMENITY_CONFIG[type];
                    const items = amenitiesData.full_amenities?.[type] || [];
                    const IconComponent = config?.icon || MapPin;
                    
                    return (
                      <Card key={type} data-testid={`amenity-card-${type}`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <IconComponent className="h-4 w-4" style={{ color: config?.color }} />
                            {config?.label || type}
                            <Badge variant="secondary" className="ml-auto">
                              {items.length}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {items.length === 0 ? (
                            <p className="text-sm text-muted-foreground">None found nearby</p>
                          ) : (
                            <div className="space-y-3">
                              {items.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="flex items-start justify-between text-sm">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{item.name}</p>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Navigation className="h-3 w-3" />
                                      {item.distance_km?.toFixed(2)} km
                                    </div>
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {item.walk_time_min} min
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Car className="h-3 w-3" />
                                      {item.drive_time_min} min
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {items.length > 5 && (
                                <p className="text-xs text-muted-foreground text-center">
                                  +{items.length - 5} more
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : showManualEntry ? (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="text-center max-w-md">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-amber-500" />
              <p className="text-lg font-medium mb-2">Could not locate property</p>
              <p className="text-sm text-muted-foreground mb-6">{geocodeError}</p>
              
              <Card className="text-left">
                <CardHeader>
                  <CardTitle className="text-base">Enter Coordinates Manually</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    You can find coordinates from Google Maps by right-clicking on the property location.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="e.g., 51.5074"
                        value={manualLat}
                        onChange={(e) => setManualLat(e.target.value)}
                        data-testid="manual-lat"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="e.g., -0.1278"
                        value={manualLng}
                        onChange={(e) => setManualLng(e.target.value)}
                        data-testid="manual-lng"
                      />
                    </div>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={fetchWithManualCoords}
                    disabled={!manualLat || !manualLng || loading}
                    data-testid="fetch-with-coords"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Load Amenities
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Could not load amenities</p>
              <Button onClick={fetchAmenities} className="mt-4">
                Retry
              </Button>
            </div>
          </div>
        )}

        <div className="border-t pt-4 mt-4 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="close-amenities-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
